import { useContext, createContext } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { getStoredStartOfWeek, type StartOfWeek } from "../utils/dateUtils";

/**
 * Fallback context used when the real AuthContext is unavailable, e.g.
 * in unit tests that mock the auth module. Returns undefined so callers
 * fall back to the stored preference.
 */
const FallbackAuthContext = createContext<
  { startOfWeek?: StartOfWeek } | undefined
>(undefined);

/**
 * US267: return the user's preferred week start day.
 *
 * Uses the value from AuthContext when available, otherwise falls back to
 * localStorage (or Monday). The fallback keeps guest pages and unit tests
 * that only mock `useAuth` working without importing the context object.
 */
export const useStartOfWeek = (): StartOfWeek => {
  const contextObject = (AuthContext as any) || FallbackAuthContext;
  const auth = useContext(contextObject) as
    | { startOfWeek?: StartOfWeek }
    | undefined;
  return auth?.startOfWeek ?? getStoredStartOfWeek();
};
